import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BaseFormComponent } from '@shared/forms/base-form.component';
import { SelectControlSingleOption } from '@shared/modules/form-controls/select-control/select-control.component';
import { TranslationsSmartCommandsApiService } from '@shared/modules/translations/data-access/translations-data-access/translations-smart-commands-api.service';
import { AdditionalTranslateFromLanguage } from '@shared/modules/smart-commands/common/translations/interfaces/responses';
import { EnumObjectValue } from '@shared/utils/common/types';
import { map, publishReplay, refCount } from 'rxjs/operators';
import { Observable } from 'rxjs';

const LanguageSelectorFormControlName = {
  from: 'from',
  to: 'to'
} as const;

@Component({
  selector: 'loop-language-selector-form',
  templateUrl: './language-selector-form.component.html',
  styleUrls: ['./language-selector-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class LanguageSelectorFormComponent extends BaseFormComponent<{
  from: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>;
  to: string;
}> {
  private readonly translationsSmartCommandsApiService: TranslationsSmartCommandsApiService = inject(
    TranslationsSmartCommandsApiService
  );

  override form: UntypedFormGroup = this.formBuilder.group({
    [LanguageSelectorFormControlName.from]: [null],
    [LanguageSelectorFormControlName.to]: [null]
  });

  protected readonly LanguageSelectorFormControlName: typeof LanguageSelectorFormControlName =
    LanguageSelectorFormControlName;

  protected readonly languageOptions$: Observable<SelectControlSingleOption[]> =
    this.translationsSmartCommandsApiService.getAvailableLanguages$().pipe(
      map(languages => {
        return languages.languages.map(language => {
          return {
            label: language.name,
            value: language.language
          } satisfies SelectControlSingleOption;
        });
      }),
      publishReplay(1),
      refCount()
    );

  protected readonly fromLanguageOptions$: Observable<SelectControlSingleOption[]> = this.languageOptions$.pipe(
    map(options => {
      return [
        {
          value: AdditionalTranslateFromLanguage.detect,
          label: 'Detect Language'
        },
        ...options
      ];
    })
  );

  setDefaults(defaults: { from: string | EnumObjectValue<typeof AdditionalTranslateFromLanguage>; to: string }): void {
    this.form.patchValue(defaults);
  }

  submitForm(): void {
    if (this.form.valid) {
      this.emitValue(this.form.value);
    }
  }
}
